import { useRef } from 'react'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { ApiError } from 'api/types'
import { useFetch } from 'providers/FetchProvider'
import { CHAT_QUERY_KEY, CHATS_QUERY_KEY } from 'api/queriesKeys'
import { useAccessToken } from 'providers/UserContextProvider'
import { Maybe } from 'types/Maybe'
import { ChatStreamError, readChatCompletionStream, resolveApiPath } from './utils'
import {
  EditChatVariables,
  DeleteChatVariables,
  SendChatMessageVariables,
  ChatApiError,
  ChatSendErrorCode,
  ChatStreamDependencies,
} from './types'

class ChatSendError extends Error {
  readonly code: ChatSendErrorCode

  constructor(code: ChatSendErrorCode) {
    super('Chat message could not be sent')
    this.name = 'ChatSendError'
    this.code = code
    Object.setPrototypeOf(this, ChatSendError.prototype)
  }
}

const isAbortError = (error: unknown) => error instanceof Error && error.name === 'AbortError'

const toChatSendErrorCode = (
  error: unknown,
  { chatId, companyId }: Pick<SendChatMessageVariables, 'chatId' | 'companyId'>,
): ChatSendErrorCode => {
  if (error instanceof ChatStreamError) return 'STREAM_ERROR'
  if (!(error instanceof ChatApiError)) return 'UNCONFIRMED'
  if (error.status === 503) return 'NOT_CONFIGURED'
  if (error.status === 401) return 'UNAUTHENTICATED'
  if (error.status === 404) {
    return !chatId && companyId !== undefined ? 'COMPANY_UNAVAILABLE' : 'NOT_FOUND'
  }
  return 'UNCONFIRMED'
}

const SEND_ERROR_KEYS: Record<ChatSendErrorCode, Maybe<string>> = {
  NOT_CONFIGURED: 'aiChat.errors.notConfigured',
  UNAUTHENTICATED: 'aiChat.errors.authentication',
  NOT_FOUND: null,
  COMPANY_UNAVAILABLE: 'aiChat.errors.companyUnavailable',
  STREAM_ERROR: 'aiChat.errors.stream',
  UNCONFIRMED: 'aiChat.errors.unconfirmed',
}

const getSendErrorKey = (code: ChatSendErrorCode) => SEND_ERROR_KEYS[code]

const buildChatRequestBody = ({
  content,
  companyId }: Pick<SendChatMessageVariables, 'content' | 'companyId'>) => ({
  content,
  stream: true,
  ...(companyId !== undefined && { companyId }),
})

const requestChatStream = async (
  { chatId, content, companyId, signal, ...handlers }: SendChatMessageVariables,
  { fetch, getAccessToken }: Omit<ChatStreamDependencies, 'refresh'>,
) => {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Access token is not available')

  const response = await fetch(
    resolveApiPath(chatId ? `/chats/${chatId}/messages` : '/chats'),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(buildChatRequestBody({ content, companyId })),
      signal,
    },
  )

  if (!response.ok) throw new ChatApiError(response.status)
  if (!response.body) throw new Error('Chat response body is missing')

  await readChatCompletionStream(response.body.getReader(), handlers)
}

const streamChatMessage = async (
  variables: SendChatMessageVariables,
  { refresh, ...dependencies }: ChatStreamDependencies,
) => {
  try {
    await requestChatStream(variables, dependencies)
  } catch (error) {
    if (isAbortError(error)) throw error
    if (error instanceof ChatApiError && error.status === 401) await refresh()
    throw new ChatSendError(toChatSendErrorCode(error, variables))
  }
}

const useSendChatMessageMutation = () => {
  const accessToken = useAccessToken()
  const accessTokenRef = useRef(accessToken)
  accessTokenRef.current = accessToken
  const { refresh } = useFetch()

  return useMutation<void, Error, SendChatMessageVariables>(
    (variables) => streamChatMessage(variables, {
      fetch: window.fetch.bind(window),
      getAccessToken: () => accessTokenRef.current,
      refresh,
    }),
  )
}

const useEditChatMutation = (
  options?: UseMutationOptions<void, ApiError, EditChatVariables>,
) => {
  const { fetch } = useFetch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation<void, ApiError, EditChatVariables>(
    async ({ id, title }) => {
      await fetch.patch(`/chats/${id}`, { title })
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([CHAT_QUERY_KEY, variables.id])
        queryClient.invalidateQueries(CHATS_QUERY_KEY)
        enqueueSnackbar(t('aiChat.history.editSuccess'), { variant: 'success' })
        options?.onSuccess?.(data, variables, context)
      },
      onError: (error, variables, context) => {
        enqueueSnackbar(t('aiChat.history.editError'), { variant: 'error' })
        options?.onError?.(error, variables, context)
      },
    },
  )
}

const useDeleteChatMutation = (
  options?: UseMutationOptions<void, ApiError, DeleteChatVariables>,
) => {
  const { fetch } = useFetch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation<void, ApiError, DeleteChatVariables>(
    async ({ id }) => {
      await fetch.delete(`/chats/${id}`)
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.removeQueries([CHAT_QUERY_KEY, variables.id], { exact: true })
        queryClient.invalidateQueries(CHATS_QUERY_KEY)
        enqueueSnackbar(t('aiChat.history.deleteSuccess'), { variant: 'success' })
        options?.onSuccess?.(data, variables, context)
      },
      onError: (error, variables, context) => {
        enqueueSnackbar(t('aiChat.history.deleteError'), { variant: 'error' })
        options?.onError?.(error, variables, context)
      },
    },
  )
}

export {
  ChatSendError,
  buildChatRequestBody,
  isAbortError,
  getSendErrorKey,
  streamChatMessage,
  toChatSendErrorCode,
  useSendChatMessageMutation,
  useEditChatMutation,
  useDeleteChatMutation,
}
